/**
 * The edit model behind the image editor (docs/proposed/IMAGE_EDITOR_V2.md §2, rewritten 2026-09-06).
 *
 * ADDITIVE, in the user's order: the state is an ordered list of operations and every tool works on
 * the image the operations before it produced — resize then crop crops the RESIZED photo; crop then
 * rotate turns the cropped one. Owner ruling 2026-09-06: "operations performed by the user should be
 * additive; they can always press Reset or undo to get the original." Consequences:
 *  - a crop is stored as FRACTIONS of its input, so an upstream change (undoing a resize, resetting a
 *    rotation) keeps it meaningful instead of pointing at stale pixels;
 *  - a resize stores its INTENT (percent / height / box / exact) and its target is derived from its
 *    input at render time, so a later crop can never squeeze the photo into a stale W×H;
 *  - the "current op" of a tool is the last (non-identity) op when it is that tool's; otherwise the
 *    first change appends a fresh op. Editing a tool adjusts what the user just did; returning to a
 *    tool after using another one starts a new step on the current image.
 * Pure functions over plain data: no DOM, no canvas.
 */

export type Tool = 'resize' | 'crop' | 'rotate' | 'flip';
export type Unit = 'px' | '%';
export type CornerFill = 'transparent' | 'white' | 'black' | 'auto-crop';
export type QuarterTurns = 0 | 1 | 2 | 3;

export interface Size { width: number; height: number; }
export interface Rect { x: number; y: number; width: number; height: number; }

/** What the user asked a resize to be; the pixels follow from the op's INPUT at render time. */
export type ResizeIntent =
    | { type: 'percent'; value: number }
    | { type: 'height'; value: number }
    | { type: 'box'; width: number; height: number }
    | { type: 'exact'; width: number; height: number; by?: 'width' | 'height' };   // `by` = the axis the user drove (typed / dragged)

export interface ResizeOp { type: 'resize'; intent: ResizeIntent | null; lockAspect: boolean; unit: Unit; }
/** `f` = the box as fractions (0..1) of the op's input; `platform` = scale the cropped region to exactly that size. */
export interface CropOp { type: 'crop'; f: { x: number; y: number; w: number; h: number }; aspect: number | null; shape: 'rect' | 'circle'; platform: Size | null; }
export interface RotateOp { type: 'rotate'; quarterTurns: QuarterTurns; fineAngle: number; cornerFill: CornerFill; }
export interface FlipOp { type: 'flip'; h: boolean; v: boolean; }
export type Op = ResizeOp | CropOp | RotateOp | FlipOp;

export interface EditState { source: Size; ops: Op[]; }

export const FINE_ANGLE_LIMIT = 45;
export const MIN_CROP_PX = 8;
const round1 = (n: number) => Math.max(1, Math.round(n));

export function initialState(source: Size): EditState {
    return { source: { width: round1(source.width), height: round1(source.height) }, ops: [] };
}

export const IDENTITY = {
    resize: { type: 'resize', intent: null, lockAspect: true, unit: 'px' } as ResizeOp,
    crop: { type: 'crop', f: { x: 0, y: 0, w: 1, h: 1 }, aspect: null, shape: 'rect', platform: null } as CropOp,
    rotate: { type: 'rotate', quarterTurns: 0, fineAngle: 0, cornerFill: 'transparent' } as RotateOp,
    flip: { type: 'flip', h: false, v: false } as FlipOp,
};

/** An op that changes nothing (a toggle that was toggled back, a crop still at the full frame). */
export function isIdentity(op: Op): boolean {
    switch (op.type) {
        case 'resize': return op.intent === null;
        case 'crop': return op.shape === 'rect' && op.platform === null && op.f.x === 0 && op.f.y === 0 && op.f.w === 1 && op.f.h === 1;
        case 'rotate': return op.quarterTurns === 0 && op.fineAngle === 0;
        case 'flip': return !op.h && !op.v;
    }
}

// ---------------------------------------------------------------- sizes along the op list

/** Bounding box of a w×h rectangle rotated by `deg`. */
export function rotatedBounds(size: Size, deg: number): Size {
    const r = Math.abs(deg) * Math.PI / 180;
    const c = Math.cos(r), sn = Math.sin(r);
    return { width: Math.round(size.width * c + size.height * sn), height: Math.round(size.width * sn + size.height * c) };
}

/** Largest axis-aligned rectangle of the SAME aspect inside a w×h image rotated by `deg` ("auto-crop corners"). */
export function insideRotated(size: Size, deg: number): Size {
    const r = Math.abs(deg) * Math.PI / 180;
    if (r === 0) return { ...size };
    const w = size.width, h = size.height, c = Math.cos(r), sn = Math.sin(r);
    const k = Math.min(w / (w * c + h * sn), h / (w * sn + h * c));
    return { width: Math.max(1, Math.floor(w * k)), height: Math.max(1, Math.floor(h * k)) };
}

export function clampRect(r: Rect, bounds: Size): Rect {
    const width = Math.min(Math.max(MIN_CROP_PX, r.width), bounds.width);
    const height = Math.min(Math.max(MIN_CROP_PX, r.height), bounds.height);
    const x = Math.min(Math.max(0, r.x), bounds.width - width);
    const y = Math.min(Math.max(0, r.y), bounds.height - height);
    return { x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height) };
}

/** Largest centred rectangle of the given aspect (w/h) inside `bounds`. */
export function centredRectWithAspect(bounds: Size, aspect: number): Rect {
    let width = bounds.width, height = Math.round(width / aspect);
    if (height > bounds.height) { height = bounds.height; width = Math.round(height * aspect); }
    return { x: Math.round((bounds.width - width) / 2), y: Math.round((bounds.height - height) / 2), width, height };
}

/** A crop op's box in the pixels of its input. */
export function cropRectPx(op: CropOp, input: Size): Rect {
    return clampRect({ x: op.f.x * input.width, y: op.f.y * input.height, width: op.f.w * input.width, height: op.f.h * input.height }, input);
}

/** The largest canvas we will ask a browser to draw — past this the tab dies rather than the resize failing. */
export const MAX_OUTPUT_EDGE = 16384;
export const MAX_OUTPUT_PIXELS = 120_000_000;

/**
 * A resize op's target for a given input. Locked box/exact intents FIT inside the box keeping the
 * input's aspect (never stretch); unlocked ones are the literal box.
 *
 * Growing past the source is allowed (owner, 2026-09-07: the toggle is gone and upscaling is simply
 * on). It costs little here because the renderer composes ONE transform from the ORIGINAL, so a
 * target above the source is a single re-sample of the real pixels, not a blow-up of an already
 * shrunken copy. Only the browser's canvas ceiling clamps it; the UI says when a target is past the
 * original's detail (see `nativeOf`).
 */
export function resizeTarget(op: ResizeOp, input: Size): Size {
    let w = input.width, h = input.height;
    const it = op.intent;
    if (it) {
        const fit = (bw: number, bh: number): [number, number] => { const k = Math.min(bw / input.width, bh / input.height); return [input.width * k, input.height * k]; };
        switch (it.type) {
            case 'percent': w = input.width * it.value / 100; h = input.height * it.value / 100; break;
            case 'height': h = it.value; w = it.value * input.width / input.height; break;
            case 'box': [w, h] = op.lockAspect ? fit(it.width, it.height) : [it.width, it.height]; break;
            case 'exact': {
                if (!op.lockAspect) { [w, h] = [it.width, it.height]; break; }
                // Locked: while the input still has the shape the target was made for, honour the axis the
                // user drove exactly (a 1px nudge must be 1px — rounding the other axis then fitting inside
                // the box lost a pixel); once the shape changed (a crop before this op, another file in a
                // batch) fit inside the box instead, so nothing is ever stretched.
                const sameShape = Math.abs(it.width / it.height - input.width / input.height) < 0.01;
                if (sameShape && it.by === 'height') { h = it.height; w = h * input.width / input.height; }
                else if (sameShape) { w = it.width; h = w * input.height / input.width; }
                else [w, h] = fit(it.width, it.height);
                break;
            }
        }
    }
    // the only clamp left is what a canvas can physically hold
    const k = Math.min(1, MAX_OUTPUT_EDGE / Math.max(w, h), Math.sqrt(MAX_OUTPUT_PIXELS / Math.max(1, w * h)));
    if (k < 1) { w = Math.floor(w * k); h = Math.floor(h * k); }   // floor: rounding up could re-cross the ceiling
    return { width: round1(w), height: round1(h) };
}

/** The size one op produces from its input. */
export function opOutput(op: Op, input: Size): Size {
    switch (op.type) {
        case 'resize': return resizeTarget(op, input);
        case 'crop': { if (op.platform) return { ...op.platform }; const r = cropRectPx(op, input); return { width: r.width, height: r.height }; }
        case 'rotate': {
            let s: Size = { ...input };
            if (op.quarterTurns % 2 === 1) s = { width: s.height, height: s.width };
            if (op.fineAngle !== 0) s = op.cornerFill === 'auto-crop' ? insideRotated(s, op.fineAngle) : rotatedBounds(s, op.fineAngle);
            return s;
        }
        case 'flip': return { ...input };
    }
}

/**
 * One op applied with the cumulative linear scale `k` from the source (1 = source pixels): resizes and
 * platform crops change k, everything else keeps it. k is what tells the UI whether a target has gone
 * past the ORIGINAL's own detail (see `nativeOf`).
 */
export function stepOutput(op: Op, input: Size, k: number): { out: Size; k: number } {
    switch (op.type) {
        case 'resize': {
            const out = resizeTarget(op, input);
            return { out, k: k * Math.sqrt((out.width * out.height) / (input.width * input.height)) };
        }
        case 'crop': {
            const r = cropRectPx(op, input);
            if (op.platform) return { out: { ...op.platform }, k: k * Math.sqrt((op.platform.width * op.platform.height) / (r.width * r.height)) };
            return { out: { width: r.width, height: r.height }, k };
        }
        default: return { out: opOutput(op, input), k };
    }
}

export interface WalkStep { op: Op; inPx: Size; outPx: Size; k: number; }
/** The first `upTo` ops with their input/output sizes and the cumulative scale after each. */
export function walk(s: EditState, upTo = s.ops.length): WalkStep[] {
    const res: WalkStep[] = []; let size: Size = { ...s.source }; let k = 1;
    for (let i = 0; i < Math.min(upTo, s.ops.length); i++) {
        const op = s.ops[i]; const r = stepOutput(op, size, k);
        res.push({ op, inPx: size, outPx: r.out, k: r.k }); size = r.out; k = r.k;
    }
    return res;
}

/** Size after the first `n` ops (n = 0 → the source). */
export function sizeAfter(s: EditState, n: number): Size {
    const w = walk(s, n);
    return w.length ? w[w.length - 1].outPx : { ...s.source };
}
/** Cumulative linear scale from the source after the first `n` ops. */
export function scaleAfter(s: EditState, n: number): number {
    const w = walk(s, n);
    return w.length ? w[w.length - 1].k : 1;
}

/** Final output size: after every op. */
export function outputSize(s: EditState): Size { return sizeAfter(s, s.ops.length); }

// ---------------------------------------------------------------- the current op of a tool

/**
 * Index of the op a tool is currently editing: the last op if it is that tool's, looking past any
 * trailing identity ops of other tools (a toggle that changed nothing must not bury the user's crop).
 * -1 = none yet; the first change appends one.
 */
export function currentIndex(s: EditState, tool: Tool): number {
    let i = s.ops.length - 1;
    while (i >= 0 && s.ops[i].type !== tool && isIdentity(s.ops[i])) i--;
    return i >= 0 && s.ops[i].type === tool ? i : -1;
}

/** The size the tool's current op works from — its input; with no current op, the current image. */
export function inputOf(s: EditState, tool: Tool): Size {
    const i = currentIndex(s, tool);
    return sizeAfter(s, i < 0 ? s.ops.length : i);
}

type OpOf<T extends Tool> = Extract<Op, { type: T }>;
function ensure<T extends Tool>(s: EditState, tool: T): { s: EditState; i: number; op: OpOf<T> } {
    let i = currentIndex(s, tool); let ops = s.ops;
    if (i < 0) { ops = [...ops, { ...(IDENTITY[tool] as unknown as OpOf<T>) }]; i = ops.length - 1; }
    return { s: { ...s, ops }, i, op: ops[i] as OpOf<T> };
}
function replaceOp(s: EditState, i: number, op: Op): EditState { const ops = s.ops.slice(); ops[i] = op; return { ...s, ops }; }

// ---------------------------------------------------------------- resize

/** The size the Resize tool works from (kept name: the "oriented" image before the current resize). */
export function orientedSize(s: EditState): Size { return inputOf(s, 'resize'); }

export function resizeView(s: EditState): ResizeOp {
    const i = currentIndex(s, 'resize');
    return i < 0 ? IDENTITY.resize : (s.ops[i] as ResizeOp);
}
function currentTarget(s: EditState): Size { return resizeTarget(resizeView(s), orientedSize(s)); }

/**
 * The pixels the ORIGINAL actually holds for what the Resize tool is looking at — its input divided by
 * the cumulative scale of the ops before it. Compare the output against this to know whether a target
 * is real detail or interpolation; `upscaleFactor` is that ratio (≤ 1 while the source still has the
 * pixels). Nothing clamps on it any more — it only drives the note in the UI.
 */
export function nativeOf(s: EditState): Size {
    const i = currentIndex(s, 'resize'); const input = orientedSize(s);
    const k = scaleAfter(s, i < 0 ? s.ops.length : i) || 1;
    return { width: Math.max(1, Math.round(input.width / k)), height: Math.max(1, Math.round(input.height / k)) };
}
export function upscaleFactor(s: EditState): number {
    const n = nativeOf(s), out = outputSize(s);
    return Math.max(out.width / n.width, out.height / n.height);
}
function setResize(s: EditState, patch: Partial<ResizeOp>): EditState { const e = ensure(s, 'resize'); return replaceOp(e.s, e.i, { ...e.op, ...patch }); }

export function setResizeWidth(s: EditState, width: number): EditState {
    const input = orientedSize(s); const w = round1(width);
    const h = resizeView(s).lockAspect ? round1(w * input.height / input.width) : currentTarget(s).height;
    return setResize(s, { intent: { type: 'exact', width: w, height: h, by: 'width' } });
}
export function setResizeHeight(s: EditState, height: number): EditState {
    const input = orientedSize(s); const h = round1(height);
    const w = resizeView(s).lockAspect ? round1(h * input.width / input.height) : currentTarget(s).width;
    return setResize(s, { intent: { type: 'exact', width: w, height: h, by: 'height' } });
}
export function setResizePercent(s: EditState, percent: number): EditState { return setResize(s, { intent: { type: 'percent', value: Math.max(1, percent) }, lockAspect: true }); }
/** "By height" presets (720p, 1080p, …): height fixed, width follows the input's aspect. */
export function setResizeByHeight(s: EditState, height: number): EditState { return setResize(s, { intent: { type: 'height', value: round1(height) }, lockAspect: true }); }
/** Fixed W×H presets: locked → fit inside the box keeping the aspect; unlocked → the exact box. */
export function setResizeBox(s: EditState, box: Size): EditState { return setResize(s, { intent: { type: 'box', width: round1(box.width), height: round1(box.height) } }); }
export function setResizeLock(s: EditState, lockAspect: boolean): EditState {
    const r = setResize(s, { lockAspect });
    const it = resizeView(r).intent;
    return lockAspect && it && it.type === 'exact' ? setResizeWidth(r, it.width) : r;
}
export function setResizeUnit(s: EditState, unit: Unit): EditState { return setResize(s, { unit }); }

/** What the W/H inputs show: the size the image ACTUALLY becomes (clamped), in px or % of the input. */
export function resizeDisplayValues(s: EditState): { width: number; height: number } {
    const input = orientedSize(s); const t = currentTarget(s);
    if (resizeView(s).unit === '%') return { width: Math.round(100 * t.width / input.width), height: Math.round(100 * t.height / input.height) };
    return t;
}

// ---------------------------------------------------------------- crop

export interface CropView { rect: Rect; aspect: number | null; shape: 'rect' | 'circle'; platform: Size | null; input: Size; }
/** The crop the tool is editing, in the pixels of its input (a pending full-frame box when none yet). */
export function cropView(s: EditState): CropView {
    const i = currentIndex(s, 'crop'); const input = inputOf(s, 'crop');
    const op = i < 0 ? IDENTITY.crop : (s.ops[i] as CropOp);
    return { rect: cropRectPx(op, input), aspect: op.aspect, shape: op.shape, platform: op.platform, input };
}
function setCrop(s: EditState, patch: Partial<CropOp>): EditState { const e = ensure(s, 'crop'); return replaceOp(e.s, e.i, { ...e.op, ...patch }); }
function fractions(r: Rect, input: Size) { const c = clampRect(r, input); return { x: c.x / input.width, y: c.y / input.height, w: c.width / input.width, h: c.height / input.height }; }

export function setCropRect(s: EditState, r: Rect): EditState { return setCrop(s, { f: fractions(r, inputOf(s, 'crop')) }); }

/** null = free; 'original' = the input's own aspect; a number = width/height. Centres the largest box of that aspect. */
export function setCropAspect(s: EditState, aspect: number | 'original' | null): EditState {
    if (aspect === null) return setCrop(s, { aspect: null });
    const input = inputOf(s, 'crop');
    const a = aspect === 'original' ? input.width / input.height : aspect;
    return setCrop(s, { aspect: a, f: fractions(centredRectWithAspect(input, a), input) });
}
/** The lock beside the aspect chips: locked holds the box's CURRENT ratio without moving it; free lets every handle move on its own. */
export function setCropLock(s: EditState, locked: boolean): EditState {
    if (!locked) return setCrop(s, { aspect: null });
    const r = cropView(s).rect;
    return setCrop(s, { aspect: r.width / Math.max(1, r.height) });
}
/** Rectangle or circle (avatar). Circle locks the box to 1:1 — the largest centred square unless it already is square. */
export function setCropShape(s: EditState, shape: 'rect' | 'circle'): EditState {
    if (shape === 'rect') return setCrop(s, { shape: 'rect' });
    const r = cropView(s).rect;
    const base = Math.abs(r.width - r.height) < 1 ? setCrop(s, { aspect: 1 }) : setCropAspect(s, 1);
    return setCrop(base, { shape: 'circle' });
}
/** A platform size (1080×1350): the box takes that aspect and the cropped region is scaled to exactly those pixels. */
export function setCropToPlatformSize(s: EditState, box: Size): EditState {
    return setCrop(setCropAspect(s, box.width / box.height), { platform: { width: round1(box.width), height: round1(box.height) } });
}
export function moveCrop(s: EditState, dx: number, dy: number): EditState {
    const r = cropView(s).rect;
    return setCropRect(s, { ...r, x: r.x + dx, y: r.y + dy });
}

export type Handle = 'tl' | 't' | 'tr' | 'r' | 'br' | 'b' | 'bl' | 'l';

/**
 * Drag a handle by (dx, dy) input pixels. Corners resize both axes, edges one axis; with an aspect
 * held, corners keep it (driven by the larger movement) and edges derive the other axis around the
 * box centre.
 */
export function dragCropHandle(s: EditState, handle: Handle, dx: number, dy: number): EditState {
    const v = cropView(s); const c = v.rect;
    let { x, y, width, height } = c;
    const left = handle.includes('l'), right = handle.includes('r'), top = handle.includes('t'), bottom = handle.includes('b');
    if (right) width += dx; if (bottom) height += dy;
    if (left) { x += dx; width -= dx; } if (top) { y += dy; height -= dy; }
    if (v.aspect) {
        const a = v.aspect;
        const isCorner = (left || right) && (top || bottom);
        if (isCorner) { if (Math.abs(dx) >= Math.abs(dy)) height = width / a; else width = height * a; }
        else if (left || right) { const h2 = width / a; y += (height - h2) / 2; height = h2; }
        else { const w2 = height * a; x += (width - w2) / 2; width = w2; }
        if (left) x = c.x + c.width - width;
        if (top) y = c.y + c.height - height;
    }
    return setCropRect(s, { x, y, width, height });
}

/** Typed crop W/H: keep the box's top-left, clamp to the input. */
export function setCropSize(s: EditState, size: Partial<Size>): EditState {
    const v = cropView(s); const c = v.rect;
    let width = size.width ?? c.width, height = size.height ?? c.height;
    if (v.aspect) { if (size.width !== undefined) height = width / v.aspect; else if (size.height !== undefined) width = height * v.aspect; }
    return setCropRect(s, { ...c, width, height });
}

// ---------------------------------------------------------------- rotate / flip

export function rotateView(s: EditState): RotateOp { const i = currentIndex(s, 'rotate'); return i < 0 ? IDENTITY.rotate : (s.ops[i] as RotateOp); }
export function flipView(s: EditState): FlipOp { const i = currentIndex(s, 'flip'); return i < 0 ? IDENTITY.flip : (s.ops[i] as FlipOp); }
function setRotate(s: EditState, patch: Partial<RotateOp>): EditState { const e = ensure(s, 'rotate'); return replaceOp(e.s, e.i, { ...e.op, ...patch }); }

export function rotateQuarter(s: EditState, direction: 'left' | 'right'): EditState {
    const q = ((rotateView(s).quarterTurns + (direction === 'right' ? 1 : 3)) % 4) as QuarterTurns;
    return setRotate(s, { quarterTurns: q });
}
export function setFineAngle(s: EditState, deg: number): EditState {
    return setRotate(s, { fineAngle: Math.max(-FINE_ANGLE_LIMIT, Math.min(FINE_ANGLE_LIMIT, Math.round(deg * 10) / 10)) });
}
export function setCornerFill(s: EditState, cornerFill: CornerFill): EditState { return setRotate(s, { cornerFill }); }
export function toggleFlip(s: EditState, axis: 'horizontal' | 'vertical'): EditState {
    const e = ensure(s, 'flip');
    return replaceOp(e.s, e.i, axis === 'horizontal' ? { ...e.op, h: !e.op.h } : { ...e.op, v: !e.op.v });
}

// ---------------------------------------------------------------- per-tool state

export function isApplied(s: EditState, tool: Tool): boolean { return s.ops.some(op => op.type === tool && !isIdentity(op)); }
/** Remove every op of that tool. Crops downstream keep their meaning (fractions); resizes re-derive from intent. */
export function resetTool(s: EditState, tool: Tool): EditState { return { ...s, ops: s.ops.filter(op => op.type !== tool) }; }
export function resetAll(s: EditState): EditState { return initialState(s.source); }

// ---------------------------------------------------------------- naming

/** `IMG_4021.jpg` + 1600×1200 + 'webp' → `IMG_4021-1600x1200.webp` (R9). */
export function outputFilename(originalName: string, size: Size, extension: string): string {
    const stem = (originalName || 'image').replace(/\.[^.]+$/, '').replace(/[\\/:*?"<>|]+/g, '_').slice(0, 120) || 'image';
    return `${stem}-${size.width}x${size.height}.${extension}`;
}
